export interface CameraPoint { x:number; y:number }
export interface BoardCameraState { width:number; height:number; zoom:number; center:CameraPoint }
const clamp=(n:number,lo:number,hi:number)=>Math.max(lo,Math.min(hi,n));
/** Presentation coordinates only. No match state or actions enter this camera. */
export class BoardCamera {
  state:BoardCameraState={width:1440,height:900,zoom:1,center:{x:720,y:450}};
  get fit(){return Math.min(this.state.width/1440,this.state.height/900);}
  get scale(){return this.fit*this.state.zoom;}
  get offset(){return {x:this.state.width/2-this.state.center.x*this.scale,y:this.state.height/2-this.state.center.y*this.scale};}
  screen(point:CameraPoint):CameraPoint{const o=this.offset;return {x:point.x*this.scale+o.x,y:point.y*this.scale+o.y};}
  board(point:CameraPoint):CameraPoint{const o=this.offset;return {x:(point.x-o.x)/this.scale,y:(point.y-o.y)/this.scale};}
  resize(width:number,height:number){this.state.width=width;this.state.height=height;this.constrain();}
  reset(){this.state.zoom=1;this.state.center={x:720,y:450};}
  zoomAt(zoom:number,anchor:CameraPoint={x:this.state.width/2,y:this.state.height/2}){
    const before=this.board(anchor);this.state.zoom=clamp(zoom,1,4);
    const after=this.board(anchor);this.state.center.x+=before.x-after.x;this.state.center.y+=before.y-after.y;this.constrain();
  }
  pan(dx:number,dy:number){this.state.center.x-=dx/this.scale;this.state.center.y-=dy/this.scale;this.constrain();}
  private constrain(){
    const halfX=this.state.width/this.scale/2,halfY=this.state.height/this.scale/2;
    this.state.center.x=halfX>=720?720:clamp(this.state.center.x,halfX,1440-halfX);
    this.state.center.y=halfY>=450?450:clamp(this.state.center.y,halfY,900-halfY);
  }
}

/** One gesture owns every finger until all lift. It cannot fall back to a tap. */
export class BoardGesture {
  private points=new Map<number,CameraPoint>();
  private moved=false; private start?:CameraPoint; private last?:CameraPoint; private distance=0;
  private suppressedUntil=0;
  constructor(readonly camera:BoardCamera,private now=()=>performance.now()){}
  get active(){return this.points.size>0;}
  get blocked(){return this.moved||this.points.size>1||this.now()<this.suppressedUntil;}
  down(id:number,p:CameraPoint){this.points.set(id,p);if(this.points.size===1){this.moved=false;this.start=p;}else this.moved=true;this.baseline();}
  move(id:number,p:CameraPoint){
    if(!this.points.has(id))return false;
    this.points.set(id,p);const current=this.measure();
    if(this.start&&Math.hypot(p.x-this.start.x,p.y-this.start.y)>8)this.moved=true;
    if(this.points.size>1){this.camera.zoomAt(this.camera.state.zoom*current.distance/Math.max(1,this.distance),this.last);this.camera.pan(current.center.x-this.last!.x,current.center.y-this.last!.y);}
    else if(this.moved&&this.camera.state.zoom>1)this.camera.pan(current.center.x-this.last!.x,current.center.y-this.last!.y);
    this.last=current.center;this.distance=current.distance;return this.moved;
  }
  up(id:number){this.points.delete(id);if(this.moved)this.suppressedUntil=this.now()+450;if(this.points.size)this.baseline();else {this.moved=false;this.start=undefined;}}
  cancel(){if(this.active)this.suppressedUntil=this.now()+450;this.points.clear();this.moved=false;this.start=undefined;}
  private measure(){const p=[...this.points.values()];return {center:p.length>1?{x:(p[0].x+p[1].x)/2,y:(p[0].y+p[1].y)/2}:p[0],distance:p.length>1?Math.hypot(p[0].x-p[1].x,p[0].y-p[1].y):0};}
  private baseline(){const m=this.measure();this.last=m.center;this.distance=m.distance;}
}
